import { cloneDeep } from 'lodash';
import omit from 'lodash-es/omit';
import * as moment from 'moment';

import type BusinessRule from '~/types/BusinessRule';

import { type ActionTypes } from '~/actions';
import { ActionKeys } from '~/actions/constants';
import { type BatchEntryExtended } from '~/types/OrderBatchEntry';
import { BatchEntryStatus } from '~/types/OrdersBatch';
import { listToMapById } from '~/utils/list';
import { newRelic } from '~/utils/newRelic/newRelic';

export type BatchEntryState = {
  associatedRules: HashMap<number, BusinessRule[]>;
  // TODO: This only supports a single batch entry per order, so no return batch entries
  byOrderId: HashMap<string, number[]>;
  data: HashMap<number, BatchEntryExtended>;
  loadState: HashMap<string, boolean>;
};

const initialState: BatchEntryState = {
  associatedRules: {},
  byOrderId: {},
  data: {},
  loadState: {},
};

// This lets us isolate some complex behavior.
// We only want to add missing content OR replace elements that have the same fulfillment rank.
const buildNextByOrderIdFromPayload = (
  payloadBatchEntries: BatchEntryExtended[],
  data: HashMap<number, BatchEntryExtended>,
  _oldByOrderId: Record<string, number[]>,
) => {
  const clonedByOrderId = cloneDeep(_oldByOrderId);

  payloadBatchEntries
    .filter((batchEntry) => !batchEntry.isReturn)
    .forEach((payloadBatchEntry) => {
      const finalOrderBatchEntryIds: number[] = [];

      const batchEntryIds = clonedByOrderId[payloadBatchEntry.orderObjectId];
      if (batchEntryIds?.length) {
        batchEntryIds.forEach((batchEntryId) => {
          const batchEntryData = data[batchEntryId];

          const shouldKeepThisBatchEntryId =
            !!batchEntryData &&
            batchEntryData.partialFulfillmentRank !== payloadBatchEntry.partialFulfillmentRank &&
            batchEntryId !== payloadBatchEntry.id;

          if (shouldKeepThisBatchEntryId) finalOrderBatchEntryIds.push(batchEntryId);
        });
      }

      finalOrderBatchEntryIds.push(payloadBatchEntry.id);
      clonedByOrderId[payloadBatchEntry.orderObjectId] = finalOrderBatchEntryIds;
    });

  return clonedByOrderId;
};

const reducer = (state: BatchEntryState = initialState, action: ActionTypes): BatchEntryState => {
  switch (action.type) {
    case ActionKeys.ORDERS_PATCH_BATCH_ENTRIES: {
      const orderObjectIds = Object.keys(action.payload.orderSelection);

      return {
        ...state,
        loadState: {
          ...state.loadState,
          ...orderObjectIds.reduce((obj, orderObjectId) => ({ ...obj, [orderObjectId]: true }), {}),
        },
      };
    }

    case ActionKeys.ORDER_BATCH_ENTRIES_MARK_PROCESSING_SHIPMENT: {
      /**
       * Optimistically flag the given batch entries as re-rating so the rate column shows
       * loading immediately. The poll overwrites these with the real entries, self-clearing it.
       */
      const existingBatchEntryIds = action.payload.batchEntryIds.filter(
        (batchEntryId) => state.data[batchEntryId],
      );
      if (existingBatchEntryIds.length === 0) return state;

      const data = { ...state.data };
      existingBatchEntryIds.forEach((batchEntryId) => {
        data[batchEntryId] = {
          ...data[batchEntryId],
          objectStatus: BatchEntryStatus.PROCESSING_SHIPMENT,
          shipmentObjectId: null,
        };
      });
      return { ...state, data };
    }

    case ActionKeys.ORDER_CLEAN: {
      const batchEntryIdsToRemove = state.byOrderId[action.orderId] ?? [];
      return {
        ...state,
        byOrderId: omit(state.byOrderId, action.orderId),
        data: omit(state.data, batchEntryIdsToRemove),
        loadState: omit(state.loadState, action.orderId),
      };
    }

    case ActionKeys.ORDER_BATCH_ENTRY_FETCH:
      // If the batch entry was already loading return existing state
      if (state.loadState[action.payload.orderObjectId]) return state;

      return {
        ...state,
        loadState: {
          ...state.loadState,
          [action.payload.orderObjectId]: true,
        },
      };

    case ActionKeys.ORDER_CREATE_PARTIAL_FULFILLMENT_REQUESTS_SUCCESS: {
      const { isReturn, order } = action.payload;

      const { partialFulfillmentRequests } = order;
      const data = { ...state.data };
      const byOrderId = {
        ...state.byOrderId,
      };

      byOrderId[order.objectId] = isReturn ? byOrderId[order.objectId] : [];

      partialFulfillmentRequests.forEach((batchEntry) => {
        data[batchEntry.id] = batchEntry;
        byOrderId[order.objectId] = [...new Set([...byOrderId[order.objectId], batchEntry.id])];
      });

      return {
        ...state,
        byOrderId,
        data,
      };
    }
    case ActionKeys.ORDER_BATCH_ENTRY_FETCH_ERROR:
      // TODO: Save error
      return {
        ...state,
        loadState: {
          ...state.loadState,
          [action.payload.orderObjectId]: false,
        },
      };

    case ActionKeys.ORDER_BATCH_ENTRIES_FETCH:
      return {
        ...state,
        loadState: {
          ...state.loadState,
          ...action.payload.orderObjectIds.reduce(
            (obj, orderObjectId) => ({ ...obj, [orderObjectId]: true }),
            {},
          ),
        },
      };

    case ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_SUCCESS:
    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_EXTRAS_SUCCESS:
    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SERVICE_LEVEL_TOKEN_SUCCESS:
    case ActionKeys.ORDERS_PATCH_BATCH_ENTRIES_SUCCESS:
    case ActionKeys.ORDERS_UPDATE_PARCEL_SUCCESS:
    case ActionKeys.ORDER_BATCH_ENTRIES_FETCH_SUCCESS:
      const batchEntries = action.payload.batchEntries;

      const nextState: BatchEntryState = {
        ...state,
        data: {
          ...state.data,
          ...batchEntries.reduce<Record<string, BatchEntryExtended>>((accumulator, batchEntry) => {
            accumulator[batchEntry.id] = batchEntry;
            return accumulator;
          }, {}),
        },
        loadState: {
          ...state.loadState,
          ...batchEntries.reduce<Record<string, boolean>>((accumulator, batchEntry) => {
            accumulator[batchEntry.orderObjectId] = false;
            return accumulator;
          }, {}),
        },
      };

      const nextByOrderId = buildNextByOrderIdFromPayload(
        batchEntries,
        nextState.data,
        nextState.byOrderId,
      );
      nextState.byOrderId = nextByOrderId;

      // Don't return the new state unless it changed. This avoids extra renders in the UI.
      return JSON.stringify(nextState) === JSON.stringify(state) ? state : nextState;
    case ActionKeys.ORDER_BATCH_ENTRIES_FETCH_ERROR:
      return {
        ...state,
        loadState: {
          ...state.loadState,
          ...action.payload.orderObjectIds.reduce(
            (obj, orderObjectId) => ({ ...obj, [orderObjectId]: false }),
            {},
          ),
        },
      };
    case ActionKeys.BATCH_ENTRY_RULES_FETCH_SUCCESS:
      return {
        ...state,
        associatedRules: {
          ...state.associatedRules,
          [action.payload.batchEntryId]: [...action.payload.rules],
        },
      };
    case ActionKeys.BATCH_ENTRY_RULES_CLEAR:
      return {
        ...state,
        associatedRules: omit(state.associatedRules, action.payload.batchEntryId),
      };
    // This is so splits render right away
    case ActionKeys.ORDERS_FETCH_SUCCESS:
      try {
        return {
          ...state,
          byOrderId: {
            ...state.byOrderId,
            ...action.payload.orders.reduce(
              (obj, order) => ({
                ...obj,
                [order.objectId]: order.partialFulfillmentRequests.map(
                  (batchEntry) => batchEntry.id,
                ),
              }),
              {},
            ),
          },
          data: {
            ...state.data,
            ...action.payload.orders.reduce(
              (obj, order) => ({
                ...obj,
                ...order.partialFulfillmentRequests.reduce(
                  (obj, batchEntry) => ({ ...obj, [batchEntry.id]: batchEntry }),
                  {},
                ),
              }),
              {},
            ),
          },
        };
      } catch (error) {
        newRelic.noticeError('Failed to update batch entries after orders fetch', { error });
        //No-op
        return state;
      }
    // This clears batch entries, mostly used so re-purchase doesn't show as a split
    case ActionKeys.ORDERS_CLEAR_BATCH_ENTRIES: {
      if (action.payload.resetBatchEntries.length === 0) return state;

      const newRankMap = action.payload.resetBatchEntries.reduce<
        Record<string, Record<number, number>>
      >(
        (obj, batchEntry) => ({
          ...obj,
          [batchEntry.orderObjectId]: {
            ...obj[batchEntry.orderObjectId],
            [batchEntry.partialFulfillmentRank]: batchEntry.id,
          },
        }),
        {},
      );

      const oldBeMap = listToMapById(action.payload.initialBatchEntries);

      const { ids, orderIds } = action.payload.initialBatchEntries.reduce(
        (obj: { ids: number[]; orderIds: string[] }, be) => ({
          ids: [...obj.ids, be.id],
          orderIds: [...obj.orderIds, be.orderObjectId],
        }),
        { ids: [], orderIds: [] },
      );

      return {
        ...state,
        byOrderId: {
          ...state.byOrderId,
          ...orderIds.reduce(
            (obj, curr) => ({
              ...obj,
              [curr]: state.byOrderId[curr].map((beId) => {
                const oldBe = oldBeMap[beId] as BatchEntryExtended;
                // Guard in case this is misused.
                // In theory the batch entries already selected this
                // will always be in the initalBatchEntries list.
                if (!oldBe) return beId;
                const oldRank = oldBe.partialFulfillmentRank;
                const newBeId = newRankMap[curr]?.[oldRank];

                return newBeId || beId;
              }),
              // [curr]: difference(state.byOrderId[curr], ids),
            }),
            {},
          ),
        },
        data: { ...omit(state.data, ids), ...listToMapById(action.payload.resetBatchEntries) },
        loadState: {
          ...state.loadState,
          ...orderIds.reduce(
            (obj, curr) => ({
              ...obj,
              [curr]: true,
            }),
            {},
          ),
        },
      };
    }

    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SHIPMENT_DATE_INITIATED: {
      const { batchEntryIds, shipmentDate } = action.payload;

      const data = { ...state.data };
      let shipDate = shipmentDate;
      if (!moment.isMoment(shipmentDate)) shipDate = moment(shipDate);

      batchEntryIds.forEach((batchEntryId) => {
        data[batchEntryId] = {
          ...data[batchEntryId],
          shipmentDate: shipDate as moment.Moment,
        };
      });

      return {
        ...state,
        data,
      };
    }

    case ActionKeys.ORDERS_BATCH_ENTRY_CONFIRM_RECIPIENT: {
      const { orderShipmentId } = action.payload;

      if (!state.data[orderShipmentId]) return state;

      const recipientAddress = state.data[orderShipmentId].addressTo;
      if (!recipientAddress) return state;

      return {
        ...state,
        data: {
          ...state.data,
          [orderShipmentId]: {
            ...state.data[orderShipmentId],
            addressTo: {
              ...recipientAddress,
              isConfirmedByUser: true,
            },
          },
        },
      };
    }

    default:
      return state;
  }
};

export default reducer;
